import { useTranslation } from 'react-i18next'
import { AuroraCirculationPumpMode } from '../enums'
import { Aurora, AuroraAssetGroup } from '../types'
import { FormControlLabel, FormGroup, ToggleButtonGroup } from '@mui/material'
import { useState } from 'react'
import { setAuroraCirculationPumpMode } from '../services/aurora'
import { useDispatch } from 'react-redux'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import AuroraCirculationPumpModeControl from './AuroraCirculationPumpModeControl'

export interface AuroraAssetGroupCirculationPumpModeControlProps {
  auroraAssetGroup: AuroraAssetGroup
  beforeSubmit?: (mode: AuroraCirculationPumpMode) => void
  afterSubmit?: (mode: AuroraCirculationPumpMode) => void
  onError?: (error: unknown) => void
}

export default function AuroraAssetGroupCirculationPumpModeControl({
  auroraAssetGroup,
  beforeSubmit,
  afterSubmit,
  onError
}: AuroraAssetGroupCirculationPumpModeControlProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('aurora')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The auroras.
   */
  const auroras = auroraAssetGroup.assets
    .filter((asset) => asset.aurora !== undefined && asset.aurora !== null)
    .map(({ aurora, ...asset }) => ({ ...aurora, asset }) as Aurora)

  /**
   * The value.
   */
  const [value, setValue] = useState<AuroraCirculationPumpMode>(AuroraCirculationPumpMode.AUTO)

  /**
   * The submitting state.
   */
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  /**
   * Handle the change event.
   */
  async function handleSubmit(mode: AuroraCirculationPumpMode) {
    try {
      setSubmitting(true)

      beforeSubmit?.(mode)

      await Promise.all(auroras.map((aurora) => setAuroraCirculationPumpMode(aurora.id, mode)))
      setValue(mode)
      dispatch(createSuccessNotification(t('setCirculationPumpModeSuccess')))

      afterSubmit?.(mode)
    } catch (error) {
      onError?.(error)
      dispatch(createErrorNotification(t('setCirculationPumpModeError')))
    } finally {
      setSubmitting(false)
    }
  }

  if (auroras.length === 0) {
    return null
  }

  return (
    <AuroraCirculationPumpModeControl
      value={value}
      loading={isSubmitting}
      onSubmit={handleSubmit}
    />
  )
}
